import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Phone, CheckCircle2, AlertCircle, Loader2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicCallbackPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState('');
  const [preferredTime, setPreferredTime] = useState('');

  useEffect(() => {
    axios.get(`${API}/public/callback/${token}`)
      .then(res => {
        setInfo(res.data);
        if (res.data.used) setSubmitted(true);
      })
      .catch(() => setError('Ce lien est invalide ou a expire.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/callback/${token}`, {
        phone: phone || null,
        preferred_time: preferredTime || null
      });
      setSubmitted(true);
      if (res.data.already_used) {
        setInfo(prev => ({ ...prev, already_used: true }));
      }
    } catch {
      setError('Une erreur est survenue. Veuillez reessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6F9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#397DFF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F6F9] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-[#BFC6CC]/20 p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#191D1E] mb-2">Lien invalide</h1>
          <p className="text-[#687175]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F9] flex items-center justify-center px-4" data-testid="public-callback-page">
      <div className="bg-white rounded-2xl shadow-sm border border-[#BFC6CC]/20 p-8 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_b5060da7-3cb1-4f34-ba83-21054a843fb3/artifacts/3oi7g9sb_pde2025.png"
            alt="Place des Energies"
            className="h-10 mx-auto"
          />
        </div>

        {submitted ? (
          <div className="text-center" data-testid="callback-success">
            <CheckCircle2 className="w-14 h-14 text-[#10D183] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#191D1E] mb-2">Demande enregistrée !</h1>
            <p className="text-[#687175] mb-6">
              Votre expert <strong className="text-[#191D1E]">{info?.agency_name}</strong> va vous recontacter
              dans les meilleurs délais concernant votre contrat <strong className="text-[#191D1E]">{info?.contract_name}</strong>.
            </p>
            {info?.agency_phone && (
              <div className="bg-[#F5F6F9] rounded-xl p-4 text-sm text-[#687175]">
                <p className="mb-1">Vous pouvez aussi nous joindre directement :</p>
                <a href={`tel:${info.agency_phone}`} className="text-[#397DFF] font-semibold flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> {info.agency_phone}
                </a>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="callback-form">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#397DFF]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-7 h-7 text-[#397DFF]" />
              </div>
              <h1 className="text-xl font-bold text-[#191D1E] mb-2">Demande de rappel</h1>
              <p className="text-[#687175]">
                Bonjour <strong className="text-[#191D1E]">{info?.client_name}</strong>,
                confirmez votre demande de rappel pour être contacté par votre expert énergie.
              </p>
            </div>

            {info?.contract_name && (
              <div className="bg-[#F5F6F9] rounded-xl p-4 mb-4 text-sm">
                <p className="text-[#687175]">Contrat concerne</p>
                <p className="font-semibold text-[#191D1E]">{info.contract_name}</p>
              </div>
            )}

            <div className="space-y-3 mb-6">
              <div>
                <label className="text-sm font-medium text-[#191D1E] block mb-1">Votre telephone</label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#939BA0]" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none"
                    data-testid="callback-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#191D1E] block mb-1">Créneau préféré (optionnel)</label>
                <div className="relative">
                  <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#939BA0]" />
                  <select
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-[#F5F6F9] border border-[#BFC6CC] text-[#191D1E] text-sm focus:ring-2 focus:ring-[#397DFF]/30 focus:border-[#397DFF] outline-none appearance-none"
                    data-testid="callback-time-select"
                  >
                    <option value="">Pas de préférence</option>
                    <option value="matin">Matin (9h-12h)</option>
                    <option value="apres-midi">Après-midi (14h-17h)</option>
                    <option value="soir">Fin de journée (17h-19h)</option>
                  </select>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-[#397DFF] hover:bg-[#397DFF]/90 text-white py-6 text-base rounded-xl"
              data-testid="confirm-callback-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Phone className="w-5 h-5 mr-2" />
              )}
              {submitting ? 'Envoi en cours...' : 'Confirmer ma demande de rappel'}
            </Button>

            <p className="text-center text-xs text-[#939BA0] mt-4">
              {info?.agency_name} — {info?.agency_phone}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicCallbackPage;
